import { useEffect, useState } from "react";
import { Download, Share, X } from "lucide-react";

const DISMISS_KEY = "tea_a2hs_dismissed";

/** Swap in the resident manifest + iOS home-screen tags while a resident page is open, and register the portal service worker. */
export function useResidentHomeScreen() {
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    const prev = link?.getAttribute("href");
    link?.setAttribute("href", "/resident-manifest.json");
    const metas = [["apple-mobile-web-app-capable", "yes"], ["mobile-web-app-capable", "yes"], ["apple-mobile-web-app-title", "The Exchange Alliance"],
      ["apple-mobile-web-app-status-bar-style", "black-translucent"]].map(([name, content]) => {
      const m = document.createElement("meta");
      m.name = name; m.content = content; m.dataset.residentPwa = "1";
      document.head.appendChild(m);
      return m;
    });
    if ("serviceWorker" in navigator) navigator.serviceWorker.register("/resident-sw.js", { scope: "/portal/" }).catch(() => {});
    return () => { if (prev) link?.setAttribute("href", prev); metas.forEach((m) => m.remove()); };
  }, []);
}

const isStandalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

/** "Add to Home Screen" tip: one-tap install on Android/Chrome, Share-button steps on iPhone/iPad. Hidden once installed or dismissed. */
export default function AddToHomeScreen() {
  const [prompt, setPrompt] = useState(null);
  const [hidden, setHidden] = useState(() => isStandalone() || localStorage.getItem(DISMISS_KEY) === "1");
  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setPrompt(e); };
    const onInstalled = () => setHidden(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener("appinstalled", onInstalled); };
  }, []);
  const ios = isIos();
  if (hidden || (!prompt && !ios)) return null;
  const dismiss = () => { localStorage.setItem(DISMISS_KEY, "1"); setHidden(true); };
  const install = async () => { prompt.prompt(); const r = await prompt.userChoice; if (r?.outcome === "accepted") setHidden(true); setPrompt(null); };
  return (
    <div data-testid="a2hs-tip" className="flex items-start gap-3 rounded-md border border-[#d4a017]/40 bg-[#d4a017]/[0.07] p-4">
      <img src="/brand/tea-icon-192.png" alt="" className="h-10 w-10 rounded-lg shrink-0" />
      <div className="flex-1 min-w-0 text-sm">
        <p className="font-semibold">Put your portal on your home screen</p>
        {prompt ? (
          <>
            <p className="text-white/60 mt-0.5">It opens like an app — one tap to your tasks and schedule.</p>
            <button type="button" onClick={install} data-testid="a2hs-install" className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-[#d4a017] text-black font-semibold px-3 py-1.5 text-xs">
              <Download className="h-3.5 w-3.5" /> Add to Home Screen
            </button>
          </>
        ) : (
          <p data-testid="a2hs-ios-steps" className="text-white/60 mt-0.5">
            In Safari, tap <Share className="inline h-3.5 w-3.5 -mt-0.5 text-white" /> <b className="text-white">Share</b>, then <b className="text-white">Add to Home Screen</b>.
            You'll sign in once more inside the new icon.
          </p>
        )}
      </div>
      <button type="button" onClick={dismiss} aria-label="Dismiss" data-testid="a2hs-dismiss" className="p-1 text-white/40 hover:text-white"><X className="h-4 w-4" /></button>
    </div>
  );
}
